/** @typedef {import('tailwindcss').Config} Config */

/** @type {Config['content']} */
const criticalFiles = [
  './src/sections/**/header.liquid',
  './src/sections/**/header-md.liquid',
  './src/snippets/**/desktop-navigation.liquid',
  './src/snippets/**/logo-snippet.liquid',
  './src/snippets/**/sticky-header.liquid',
  './src/snippets/**/css-sticky.liquid',
  './src/snippets/**/hamburger-menu.liquid',
  './src/snippets/**/navigation-mega-menu.liquid',
  './src/snippets/**/icon-search.liquid',
  './src/snippets/**/icon-close.liquid',
  './src/snippets/**/header-cart-button.liquid',
  './src/snippets/**/section-wrapper-classes.liquid',
  './src/snippets/**/case-mb.liquid',
  './src/snippets/**/case-mx.liquid',
  './src/snippets/**/case-border.liquid',
  './src/snippets/**/navigation-snippet.liquid',
  './src/snippets/**/icon-arrow.liquid',
  './src/snippets/**/cart-icon-bubble.liquid',
  './src/snippets/**/currency-selector.liquid',

  './src/sections/**/hero-banner.liquid',
  './src/snippets/**/hero-media-image.liquid',
  './src/snippets/**/hero-media-video-url.liquid',
  './src/snippets/**/hero-media-video-youtube.liquid',
  './src/snippets/**/image-adaptive.liquid',
  './src/snippets/**/image-responsive.liquid',
  './src/snippets/**/hero-content.liquid',
  './src/snippets/**/hero-content-buttons.liquid',
  './src/snippets/**/hero-content-button.liquid',
  './src/snippets/**/button.liquid',
  './src/snippets/**/case-button-class.liquid',
  './src/snippets/**/case-aspect-ratio.liquid',

  './src/sections/**/featured-collection.liquid',
  './src/snippets/**/grid-layout.liquid',
  './src/snippets/**/case-grid-flex-basis.liquid',
  './src/snippets/**/fs-seo-text.liquid',
  './src/snippets/**/carousel.liquid',
  './src/snippets/**/card-generic.liquid',
  './src/snippets/**/case-gap.liquid',
  './src/snippets/**/case-gap-y.liquid',
  './src/snippets/**/case-gap-x.liquid',
  './src/snippets/**/case-pb.liquid',
  './src/snippets/**/case-pt.liquid',
  './src/snippets/**/case-align-text.liquid',
  './src/snippets/**/case-align-items.liquid',
  './src/snippets/**/case-align-justify.liquid',
  './src/snippets/**/case-columns.liquid',
  './src/snippets/**/case-col-span.liquid',
  './src/snippets/**/case-row-span.liquid',
  './src/snippets/**/card/*.liquid',
  './src/snippets/**/layout-section-carousel-peek.liquid',
  './src/snippets/**/layout-section-standard.liquid',
  './src/snippets/**/case-py.liquid',
  './src/snippets/**/case-px.liquid',
  './src/snippets/**/case-section-width.liquid',
  './src/snippets/**/case-height.liquid',
  './src/snippets/**/case-min-height.liquid',
  './src/snippets/**/section-wrapper-classes.liquid',
  './src/snippets/**/section-container-classes.liquid',
  './src/snippets/**/section-spacing-classes.liquid',
  './src/snippets/**/section-container-gap-classes.liquid',
  './src/snippets/**/section-bg-img.liquid',
  './src/snippets/**/case-icon-size.liquid',
  './src/snippets/**/case-width.liquid',
  './src/snippets/**/case-min-width.liquid',
  './src/snippets/**/territory-selector.liquid',
  './src/snippets/**/grid-carousel.liquid',
  './src/snippets/**/page-background.liquid',
]

/** @satisfies {Config} */
const common = /** @type {const} */ ({
  content: [],
  theme: {
    extend: {
      animation: {
        'ping-small': 'ping 1s cubic-bezier(0,0,0,0) infinite'
      },
      inset: {
        unset: 'unset'
      },
      screens: {
        xs: '480px',
        sm: '640px',
        md: '768px',
        lg: '1024px',
        xl: '1280px',
        '2xl': '1536px',
        '3xl': '1792px',
      },
      spacing: {
        '1em': '1em',
        '0.8em': '0.8em',
        '0.35em': '0.35em',
        'var-ratio': 'var(--ratio,100%)',
        'var-ratio-md': 'var(--ratio-md,100%)'
      },
      columns: {
        'var-cols': 'var(--cols)',
        'var-cols-md': 'var(--cols-md)',
        'var-cols-lg': 'var(--cols-lg)'
      },
      display: ['group-hover'],
      backgroundImage: {
        button: 'var(--background-image)'
      },
      textColor: {
        th: {
          base: 'var(--color-display-dark, #0f0f0f)',
          muted: 'var(--color-display-base, rgba(0,0,0,.75))',
          inverted: 'var(--color-display-light, white)',
          button: {
            color: 'var(--button-color)',
            base: 'var(--button-base, white)',
            muted: 'var(--button-muted, rgba(255,255,255,.83))',
            inverted: 'var(--button-inverted, var(--button-bg))',
            hover: {
              color: 'var(--button-hover-color)',
            }
          }
        }
      },
      opacity: {
        10: '.1',
        20: '.2',
        30: '.3',
        40: '.4',
        50: '.5',
        60: '.6',
        70: '.7',
        80: '.8',
        90: '.9',
      },
      borderColor: {
        'default-color': 'var(--border-default-color)'
      },
      borderRadius: {
        'default-radius': 'var(--border-default-radius)',
        button: 'var(--button-all-border-radius)',
        xs: '8px',
        sm: '12px',
        md: '16px',
        lg: '24px',
        xl: '36px'
      },
      borderWidth: {
        none: '0',
        'default-width': 'var(--border-default-width)',
        'default-width-narrow': 'var(--border-default-width-narrow)',
        'default-width-wide': 'var(--border-default-width-wide)',
        button: 'var(--border-button-width)'
      },
      flexBasis: {
        30: '30%',
        35: '35%',
        40: '40%',
        45: '45%',
        50: '50%',
        55: '55%',
        60: '60%',
        65: '65%',
        70: '70%'
      },
      maxWidth: {
        full: '100%',
        '2xs': '24rem',
        xs: '36rem',
        sm: '48rem',
        md: '61rem',
        lg: '75rem',
        xl: '100rem',
        'pdp-thumb-wrapper': 'calc(var(--pdp-thumb-size) - 6px)',
      },
      width: {
        '1em': '1em',
        '4ch': '4ch',
        '8ch': '8ch',
        'min-click-space': '2.75rem',
      },
      height: {
        'min-click-space': '2.75rem',
      },
      minWidth: {
        'min-click-space': '2.75rem',
      },
      minHeight: {
        'min-click-space': '2.75rem',
      },
      maxHeight: {
        'pdp-thumb-wrapper': 'calc(var(--pdp-thumb-size) - 6px)'
      },
      gap: {
        none: '0rem',
        'var-grid-gap': 'var(--grid-gap)',
        'var-grid-gap-x': 'var(--grid-gap-x)',
        'sm-2xs': 'var(--gap-sm-2xs)',
        'sm-xs': 'var(--gap-sm-xs)',
        'sm-sm': 'var(--gap-sm-sm)',
        'sm-md': 'var(--gap-sm-md)',
        'sm-lg': 'var(--gap-sm-lg)',
        'sm-xl': 'var(--gap-sm-xl)',
        'sm-2xl': 'var(--gap-sm-2xl)',
        'lg-2xs': 'var(--gap-lg-2xs)',
        'lg-xs': 'var(--gap-lg-xs)',
        'lg-sm': 'var(--gap-lg-sm)',
        'lg-md': 'var(--gap-lg-md)',
        'lg-lg': 'var(--gap-lg-lg)',
        'lg-xl': 'var(--gap-lg-xl)',
        'lg-2xl': 'var(--gap-lg-2xl)',
        'container-2xs': 'var(--gap-container-2xs)',
        'container-xs': 'var(--gap-container-xs)',
        'container-sm': 'var(--gap-container-sm)',
        'container-md': 'var(--gap-container-md)',
        'container-lg': 'var(--gap-container-lg)',
        'container-xl': 'var(--gap-container-xl)',
        'container-2xl': 'var(--gap-container-2xl)'
      },
      margin: {
        none: '0rem',
        '3px': '3px',
        'sm-2xs': 'var(--margin-sm-2xs)',
        'sm-xs': 'var(--margin-sm-xs)',
        'sm-sm': 'var(--margin-sm-sm)',
        'sm-md': 'var(--margin-sm-md)',
        'sm-lg': 'var(--margin-sm-lg)',
        'sm-xl': 'var(--margin-sm-xl)',
        'sm-2xl': 'var(--margin-sm-2xl)',
        'lg-2xs': 'var(--margin-lg-2xs)',
        'lg-xs': 'var(--margin-lg-xs)',
        'lg-sm': 'var(--margin-lg-sm)',
        'lg-md': 'var(--margin-lg-md)',
        'lg-lg': 'var(--margin-lg-lg)',
        'lg-xl': 'var(--margin-lg-xl)',
        'lg-2xl': 'var(--margin-lg-2xl)',
        'container-2xs': 'var(--margin-container-2xs)',
        'container-xs': 'var(--margin-container-xs)',
        'container-sm': 'var(--margin-container-sm)',
        'container-md': 'var(--margin-container-md)',
        'container-lg': 'var(--margin-container-lg)',
        'container-xl': 'var(--margin-container-xl)',
        'container-2xl': 'var(--margin-container-2xl)'
      },
      padding: {
        none: '0rem',
        '3px': '3px',
        'xs-2xs': 'var(--padding-xs-2xs)',
        'xs-xs': 'var(--padding-xs-xs)',
        'xs-sm': 'var(--padding-xs-sm)',
        'xs-md': 'var(--padding-xs-md)',
        'xs-lg': 'var(--padding-xs-lg)',
        'xs-xl': 'var(--padding-xs-xl)',
        'xs-2xl': 'var(--padding-xs-2xl)',
        'sm-2xs': 'var(--padding-sm-2xs)',
        'sm-xs': 'var(--padding-sm-xs)',
        'sm-sm': 'var(--padding-sm-sm)',
        'sm-md': 'var(--padding-sm-md)',
        'sm-lg': 'var(--padding-sm-lg)',
        'sm-xl': 'var(--padding-sm-xl)',
        'sm-2xl': 'var(--padding-sm-2xl)',
        'lg-2xs': 'var(--padding-lg-2xs)',
        'lg-xs': 'var(--padding-lg-xs)',
        'lg-sm': 'var(--padding-lg-sm)',
        'lg-md': 'var(--padding-lg-md)',
        'lg-lg': 'var(--padding-lg-lg)',
        'lg-xl': 'var(--padding-lg-xl)',
        'lg-2xl': 'var(--padding-lg-2xl)',
        'container-2xs': 'var(--padding-container-2xs)',
        'container-xs': 'var(--padding-container-xs)',
        'container-sm': 'var(--padding-container-sm)',
        'container-md': 'var(--padding-container-md)',
        'container-lg': 'var(--padding-container-lg)',
        'container-xl': 'var(--padding-container-xl)',
        'container-2xl': 'var(--padding-container-2xl)',
        'form-select': 'var(--select-pad-r)'
      },
      gridTemplateColumns: {
        'sm-before': '25%,1fr',
        'sm-after': '1fr,25%',
        'md-before': '33%,1fr',
        'md-after': '1fr,33%',
        'lg-before': '40%,1fr',
        'lg-after': '1fr,40%',
        'xl-before': '50%,1fr',
        'xl-after': '1fr,50%',
        'pdp-container-left': 'var(--pdp-thumb-size),1fr',
        'pdp-media-width-left-md': 'var(--media-width-md),1fr',
        'pdp-media-width-left-lg': 'var(--media-width-lg),1fr',
        'pdp-media-width-right-md': '1fr,var(--media-width-md)',
        'pdp-media-width-right-lg': '1fr,var(--media-width-lg)'
      },
      gridTemplateRows: {
        'pdp-container-bottom': 'max-content,var(--pdp-thumb-size)'
      },
      zIndex: {
        60: '60',
        70: '70',
        80: '80',
        90: '90',
        100: '100',
        110: '110',
        120: '120',
        130: '130',
        140: '140',
        150: '150'
      }
    },
  },
  safelist: [],
  variants: {
    extend: {},
  },
  plugins: [
    require('@tailwindcss/forms'),
  ],
  future: {
    hoverOnlyWhenSupported: true,
  },
})

module.exports = {
  criticalFiles,
  common,
}
